import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "../components/Icon";

export type ToastTone = "success" | "error" | "info";

interface ToastInput {
  title: string;
  message?: string;
  tone?: ToastTone;
  duration?: number;
}

interface ToastItem extends Required<Pick<ToastInput, "title" | "tone" | "duration">> {
  id: number;
  message?: string;
}

interface ToastContextValue {
  pushToast: (toast: ToastInput) => number;
  dismissToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function ToastCard({ toast, dismiss }: { toast: ToastItem; dismiss: (id: number) => void }) {
  useEffect(() => {
    if (toast.duration <= 0) return;
    const timer = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timer);
  }, [dismiss, toast.duration, toast.id]);

  return (
    <article className={`toast-card toast-card--${toast.tone}`} role={toast.tone === "error" ? "alert" : "status"}>
      <span className="toast-card__icon">
        <Icon name={toast.tone === "success" ? "check" : toast.tone === "error" ? "alert" : "pulse"} size={18} />
      </span>
      <div><b>{toast.title}</b>{toast.message ? <p>{toast.message}</p> : null}</div>
      <button type="button" onClick={() => dismiss(toast.id)} aria-label="关闭提示">×</button>
    </article>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  function dismissToast(id: number) {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }

  function pushToast(input: ToastInput) {
    const id = nextId.current++;
    const toast: ToastItem = {
      id,
      title: input.title,
      message: input.message,
      tone: input.tone || "info",
      duration: input.duration ?? (input.tone === "error" ? 7000 : 4200),
    };
    setToasts((current) => [...current.slice(-3), toast]);
    return id;
  }

  return (
    <ToastContext.Provider value={{ pushToast, dismissToast }}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-relevant="additions">
        {toasts.map((toast) => <ToastCard key={toast.id} toast={toast} dismiss={dismissToast} />)}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside ToastProvider");
  return value;
}
